import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  FolderKanban,
  Home,
} from 'lucide-react';
import {
  CATEGORY_TOOL_HUBS,
  getDesignToolTabHref,
} from '../data/categoryToolHubs';

const TOOL_GROUPS = [
  {
    id: 'room-interior',
    label: 'Interior AI Tools',
    toolIds: ['full-decoration', 'furniture-change', 'interior-3d-model', 'light-renovation'],
  },
  {
    id: 'exterior',
    label: 'Exterior AI Tools',
    toolIds: ['building-exterior', 'garden-design'],
  },
  {
    id: 'floor-plan',
    label: 'Architecture AI Tools',
    toolIds: [
      'floor-plan-3d',
      'floor-plan-room-preview',
      'floor-plan-create',
      'draw-floor-plan',
    ],
  },
];

function isActivePath(currentPath, to) {
  if (!to) return false;
  if (to === '/projects/new') {
    return currentPath === '/projects/new' || currentPath.startsWith('/projects/new/');
  }
  if (to === '/projects') {
    if (currentPath === '/projects') return true;
    if (
      currentPath.startsWith('/projects/') &&
      !currentPath.startsWith('/projects/new') &&
      !currentPath.startsWith('/projects/ai-')
    ) {
      return true;
    }
    return false;
  }
  return currentPath === to || currentPath.startsWith(`${to}/`);
}

function SidebarToolGroup({
  group,
  currentPath,
  onNavigate,
  expanded,
  onToggle,
  compact = false,
  onExpandSidebar,
}) {
  const hub = CATEGORY_TOOL_HUBS[group.id];
  const GroupIcon = hub?.tabIcon;
  const tabHref = getDesignToolTabHref(group.id);
  const subitemsId = `app-sidebar-subitems-${group.id}`;

  const handleToggle = () => {
    if (compact && onExpandSidebar) {
      onExpandSidebar(group.id);
      return;
    }
    onToggle();
  };

  return (
    <div className={`app-sidebar-group${expanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="app-sidebar-group-toggle app-sidebar-group-toggle--button"
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-controls={subitemsId}
        title={compact ? group.label : undefined}
      >
        {GroupIcon ? (
          <span className="app-sidebar-group-icon" aria-hidden>
            <GroupIcon size={15} strokeWidth={2} />
          </span>
        ) : null}
        <span className="app-sidebar-group-label">{group.label}</span>
        <ChevronDown size={16} strokeWidth={2} className="app-sidebar-group-chevron" aria-hidden />
      </button>

      <div
        id={subitemsId}
        className="app-sidebar-subitems"
        hidden={!expanded || compact}
      >
        {group.toolIds.map((toolId) => {
            const tool = hub?.tools.find((t) => t.id === toolId);
            if (!tool || tool.hidden) return null;

            const ToolIcon = tool.icon;
            const subHref = tool.route && !tool.comingSoon ? tool.route : tabHref;
            const routePath = tool.route?.split('?')[0] ?? '';
            const projectRoutePath = routePath.startsWith('/ai-tools/')
              ? routePath.replace('/ai-tools/', '/projects/')
              : routePath;
            const subActive =
              tool.route &&
              !tool.comingSoon &&
              (currentPath === routePath ||
                currentPath.startsWith(`${routePath}/`) ||
                currentPath === projectRoutePath ||
                currentPath.startsWith(`${projectRoutePath}/`));

            return (
              <Link
                key={toolId}
                to={subHref}
                className={`app-sidebar-link app-sidebar-subitem${subActive ? ' is-active' : ''}`}
                onClick={onNavigate}
              >
                <ToolIcon size={16} strokeWidth={2} aria-hidden />
                <span>{tool.title}</span>
                {tool.previewBadge === 'free' ? (
                  <span className="app-sidebar-free-badge">FREE</span>
                ) : null}
              </Link>
            );
          })}
      </div>
    </div>
  );
}

function SidebarProjectsLink({ currentPath, onNavigate, compact = false }) {
  const active = isActivePath(currentPath, '/projects');

  return (
    <Link
      to="/projects"
      className={`app-sidebar-link app-sidebar-group-toggle app-sidebar-projects-link${active ? ' is-active' : ''}`}
      onClick={onNavigate}
      title={compact ? 'Projects' : undefined}
    >
      <span className="app-sidebar-group-icon" aria-hidden>
        <FolderKanban size={15} strokeWidth={2} />
      </span>
      <span className="app-sidebar-group-label">Projects</span>
    </Link>
  );
}

function SidebarHomeLink({ currentPath, onNavigate, compact = false }) {
  const active = currentPath === '/';

  return (
    <Link
      to="/"
      className={`app-sidebar-link app-sidebar-group-toggle app-sidebar-home-link${active ? ' is-active' : ''}`}
      onClick={onNavigate}
      title={compact ? 'Home' : undefined}
    >
      <span className="app-sidebar-group-icon" aria-hidden>
        <Home size={15} strokeWidth={2} />
      </span>
      <span className="app-sidebar-group-label">Home</span>
    </Link>
  );
}

function useMobileSidebarLayout() {
  const getMatches = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 992px)').matches;

  const [isMobileLayout, setIsMobileLayout] = useState(getMatches);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 992px)');
    const handleChange = (event) => setIsMobileLayout(event.matches);

    setIsMobileLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobileLayout;
}

function AppSidebar({
  mobileOpen,
  onClose,
  pageClassName = '',
  collapsed = false,
  onCollapsedChange,
}) {
  const { pathname } = useLocation();
  const [expandedGroups, setExpandedGroups] = useState(() => new Set(TOOL_GROUPS.map((group) => group.id)));

  const isCategoryHubPage = pageClassName.includes('category-hub-page');
  const isSidebarCardLayout = isCategoryHubPage;
  const isMobileLayout = useMobileSidebarLayout();
  const compact = collapsed && !isMobileLayout;

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleNavigate = () => {
    if (onClose) onClose();
  };

  const handleExpandSidebarFromGroup = (groupId) => {
    if (onCollapsedChange) onCollapsedChange(false);
    setExpandedGroups((prev) => new Set(prev).add(groupId));
  };

  const sidebarContent = (
    <>
      <div
        className={`app-sidebar-overlay${mobileOpen ? ' is-visible' : ''}`}
        onClick={onClose}
        aria-hidden={!mobileOpen}
      />
      <aside
        id="app-sidebar"
        className={`app-sidebar${mobileOpen ? ' app-sidebar--open' : ''}${
          isSidebarCardLayout ? ' app-sidebar--card-layout' : ''
        }${compact ? ' app-sidebar--collapsed' : ''}`}
        aria-label="App navigation"
      >
        <div className="app-sidebar-panel">
          <nav className="app-sidebar-nav">
            {onCollapsedChange && !isMobileLayout ? (
              <div className="app-sidebar-section app-sidebar-section--collapse">
                <div className="app-sidebar-section-items">
                  <button
                    type="button"
                    className="app-sidebar-collapse-toggle app-sidebar-link app-sidebar-group-toggle app-sidebar-collapse-link"
                    onClick={() => onCollapsedChange(!collapsed)}
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    aria-pressed={collapsed}
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  >
                    <span className="app-sidebar-group-icon" aria-hidden>
                      {collapsed ? (
                        <PanelLeftOpen size={15} strokeWidth={2} />
                      ) : (
                        <PanelLeftClose size={15} strokeWidth={2} />
                      )}
                    </span>
                  </button>
                </div>
              </div>
            ) : null}

            <div className="app-sidebar-section">
              {!isMobileLayout ? (
                <div className="app-sidebar-section-items app-sidebar-section-items--home">
                  <SidebarHomeLink
                    currentPath={pathname}
                    onNavigate={handleNavigate}
                    compact={compact}
                  />
                </div>
              ) : null}
              <div className="app-sidebar-section-items app-sidebar-section-items--groups">
                {TOOL_GROUPS.map((group) => (
                  <SidebarToolGroup
                    key={group.id}
                    group={group}
                    currentPath={pathname}
                    onNavigate={handleNavigate}
                    expanded={expandedGroups.has(group.id)}
                    onToggle={() => toggleGroup(group.id)}
                    compact={compact}
                    onExpandSidebar={handleExpandSidebarFromGroup}
                  />
                ))}
              </div>
            </div>

            <div className="app-sidebar-section app-sidebar-section--projects">
              <div className="app-sidebar-section-items">
                <SidebarProjectsLink currentPath={pathname} onNavigate={handleNavigate} compact={compact} />
              </div>
            </div>
          </nav>
        </div>
      </aside>
    </>
  );

  if (isMobileLayout && typeof document !== 'undefined') {
    return createPortal(sidebarContent, document.body);
  }

  return sidebarContent;
}

export default AppSidebar;
